import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@app/components/button/button.component';

@Component({
  selector: 'app-file-dropzone',
  standalone: true,
  templateUrl: './file-dropzone.component.html',
  styleUrls: ['./file-dropzone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, ButtonComponent],
})
export class FileDropzoneComponent {
  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement> | null = null;

  @Input() accept = 'image/*';
  @Input() multiple = true;
  @Input() dragLabelKey = 'UPLOAD_IMAGES.MODAL_FILE_DRAG';
  @Input() hintLabelKey = 'UPLOAD_IMAGES.MODAL_FILE_HINT';
  @Input() buttonLabelKey = 'UPLOAD_IMAGES.MODAL_FILE_BUTTON';
  @Input() limitLabelKey = 'UPLOAD_IMAGES.MODAL_FILE_LIMIT_MULTIPLE';

  @Output() filesSelected = new EventEmitter<File[]>();

  dragActive = false;
  private dragCounter = 0;
  private wasDragOperation = false;

  constructor(private cdr: ChangeDetectorRef) {}

  triggerFileSelect(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.wasDragOperation) {
      this.wasDragOperation = false;
      return;
    }

    requestAnimationFrame(() => {
      if (this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.click();
      }
    });
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (files.length > 0) {
      this.filesSelected.emit(files);
    }
    input.value = ''; // Reset input
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.wasDragOperation = true;

    if (!this.dragActive) {
      this.dragActive = true;
      this.cdr.markForCheck();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.types.includes('Files')) {
      this.wasDragOperation = true;
      if (!this.dragActive) {
        this.dragActive = true;
        this.cdr.markForCheck();
      }
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragCounter--;

    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.dragActive = false;
      this.cdr.markForCheck();
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragCounter = 0;
    this.dragActive = false;
    this.wasDragOperation = true;

    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    if (files.length > 0) {
      this.filesSelected.emit(files);
    }

    setTimeout(() => {
      this.wasDragOperation = false;
    }, 100);

    this.cdr.markForCheck();
  }

  onDragEnd(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.dragActive = false;

    setTimeout(() => {
      this.wasDragOperation = false;
    }, 100);

    this.cdr.markForCheck();
  }
}
